'use client';

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '@/app/lib/axios';
import { User } from '@/types/user';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    error: string | null;
    setUser: (user: (prev: any) => any) => void;
    logout: () => void;
    isKickProvider: boolean,
    refreshUser: () => void;
    updateBalance: (balance: number) => void;
    updateSokoBalance: (balance: number) => void;
    updateBalances: (points: number, soko_points: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isKickProvider, setIsKickProvider] = useState(false)

    // Step 1: Handle token from redirect URL
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');

        if (token) {
            localStorage.setItem('token', token);

            // Clean URL so token disappears
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
        }
    }, []);

    // Step 2: Fetch user with token
    useEffect(() => {
        const fetchUser = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const res = await api.get<User>('/user', {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setUser(res.data);
                setIsKickProvider(res.data.provider === 'kick');
            } catch (err) {
                console.error('Failed to fetch user:', err);
                setUser(null);
                setError('Failed to fetch user');
            } finally {
                setLoading(false);
            }
        };

        fetchUser();
    }, []);

    // Step 3: Logout function
    const logout = async () => {
        const token = localStorage.getItem('token');

        try {
            if (token) {
                await api.post('/logout', null, {
                    headers: { Authorization: `Bearer ${token}` },
                });
            }
        } catch (err) {
            console.warn('Logout error:', err);
        } finally {
            localStorage.removeItem('token');
            setUser(null);
        }
    };

    const refreshUser = async () => {
        const token = localStorage.getItem('token');
        if (!token) return;

        try {
            const res = await api.get<User>('/user', {
                headers: { Authorization: `Bearer ${token}` },
            });
            setUser(res.data);
            setIsKickProvider(res.data.provider === 'kick');
        } catch (err) {
            console.error('Failed to refresh user:', err);
            setUser(null);
            setError('Failed to fetch user');
        }
    };

    const updateBalance = (balance: number) => {
        setUser(prev =>
            prev ? { ...prev, points: balance } : prev
        );
    };

    // Gambling (SokoPlay) balance — games bet and pay out in soko_points.
    const updateSokoBalance = (balance: number) => {
        setUser(prev =>
            prev ? { ...prev, soko_points: balance } : prev
        );
    };

    // Points ⇄ Soko Points exchange moves both wallets in one response.
    const updateBalances = (points: number, soko_points: number) => {
        setUser(prev =>
            prev ? { ...prev, points, soko_points } : prev
        );
    };

    return (
        <AuthContext.Provider value={{ user, loading, error, setUser, logout, isKickProvider, refreshUser, updateBalance, updateSokoBalance, updateBalances }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
